import React from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { getTenantPortalSession, clearTenantPortalSession } from './tenantPortalSession';
import { useTenantPortalGuard } from './useTenantPortalGuard';
import { TenantPortalContext } from './TenantPortalContext';

function propertyFromRow(row) {
  const rel = row?.properties;
  return Array.isArray(rel) ? rel[0] : rel;
}

export default function TenantPortalLayout() {
  const navigate = useNavigate();
  useTenantPortalGuard();
  const session = getTenantPortalSession();

  const [profile, setProfile] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState('');

  const refresh = React.useCallback(async () => {
    if (!session?.tenantId || !supabase) {
      setProfile(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError('');

    const { data: tenantRow, error: tenantError } = await supabase
      .from('prospective_tenants')
      .select(
        `
        id,
        full_name,
        email,
        phone,
        rent_status,
        rent_due_day_of_month,
        move_in_date,
        move_out_date,
        property_id,
        room_label,
        created_at
      `
      )
      .eq('id', session.tenantId)
      .single();

    if (tenantError || !tenantRow) {
      setLoadError(tenantError?.message || 'Could not load your tenant profile.');
      setProfile(null);
      setLoading(false);
      return;
    }

    let merged = { ...tenantRow, properties: null };
    if (tenantRow.property_id) {
      // Same row as /properties/:id/edit — PropertyNew loads with .from('properties').select('*').eq('id', id).single()
      const { data: propRow, error: propError } = await supabase
        .from('properties')
        .select('*')
        .eq('id', tenantRow.property_id)
        .single();
      if (!propError && propRow) {
        merged = { ...tenantRow, properties: propRow };
      }
    }

    setProfile(merged);
    setLoading(false);
  }, [session?.tenantId]);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  React.useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [refresh]);

  const contextValue = React.useMemo(() => {
    if (!profile || !session) return null;
    return {
      profile,
      session,
      property: propertyFromRow(profile),
      refresh,
    };
  }, [profile, session, refresh]);

  if (!session) {
    return (
      <div className="tenant-root">
        <p className="tenant-subtitle" style={{ padding: 24 }}>
          Redirecting to sign in…
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="tenant-root">
        <p className="tenant-subtitle" style={{ padding: 24 }}>
          Loading your portal…
        </p>
      </div>
    );
  }

  if (loadError || !profile) {
    return (
      <div className="tenant-root">
        <p className="properties-save-error" role="alert" style={{ margin: 24 }}>
          {loadError || 'Your tenant record could not be loaded.'}
        </p>
        <button
          type="button"
          className="btn-primary"
          style={{ marginLeft: 24 }}
          onClick={() => {
            clearTenantPortalSession();
            navigate('/admin', { replace: true });
          }}
        >
          Back to sign in
        </button>
      </div>
    );
  }

  if (!contextValue) {
    return (
      <div className="tenant-root">
        <p className="tenant-subtitle" style={{ padding: 24 }}>
          Loading your portal…
        </p>
      </div>
    );
  }

  return (
    <TenantPortalContext.Provider value={contextValue}>
      <Outlet />
    </TenantPortalContext.Provider>
  );
}
